import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import {
  formatLieferKw,
  isoWeek,
  isoWeekYear,
  mondayOfIsoWeek,
  type MaLieferKwRange,
} from "@/lib/maLieferKw";

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
const MONTH_FORMATTER = new Intl.DateTimeFormat("de-DE", {
  month: "long",
  year: "numeric",
});

interface WeekCell {
  week: number;
  year: number;
  monday: Date;
  days: Date[];
}

interface MaLieferKwRangePickerProps {
  value: MaLieferKwRange | null;
  disabled?: boolean;
  onApply: (range: MaLieferKwRange) => void;
}

function startOfMonth(year: number, monthIndex: number): Date {
  return new Date(year, monthIndex, 1);
}

function addMonths(base: Date, delta: number): Date {
  return new Date(base.getFullYear(), base.getMonth() + delta, 1);
}

function sameWeek(a: { week: number; year: number }, b: { week: number; year: number }) {
  return a.week === b.week && a.year === b.year;
}

function weekKey(w: { week: number; year: number }) {
  return `${w.year}-W${w.week}`;
}

function compareWeek(
  a: { week: number; year: number },
  b: { week: number; year: number },
): number {
  const am = mondayOfIsoWeek(a.year, a.week).getTime();
  const bm = mondayOfIsoWeek(b.year, b.week).getTime();
  return am - bm;
}

function buildMonthWeeks(viewMonth: Date): WeekCell[] {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const first = startOfMonth(year, month);
  const dayNr = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - dayNr);

  const weeks: WeekCell[] = [];
  for (let row = 0; row < 6; row += 1) {
    const monday = new Date(gridStart);
    monday.setDate(gridStart.getDate() + row * 7);
    const days: Date[] = [];
    for (let d = 0; d < 7; d += 1) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + d);
      days.push(day);
    }
    const mid = days[3] ?? monday;
    weeks.push({
      week: isoWeek(mid),
      year: isoWeekYear(mid),
      monday,
      days,
    });
  }
  return weeks;
}

function isInSelectedRange(
  cell: { week: number; year: number },
  draftVon: { week: number; year: number } | null,
  draftBis: { week: number; year: number } | null,
): boolean {
  if (!draftVon) return false;
  const end = draftBis ?? draftVon;
  const lo = compareWeek(draftVon, end) <= 0 ? draftVon : end;
  const hi = compareWeek(draftVon, end) <= 0 ? end : draftVon;
  return compareWeek(cell, lo) >= 0 && compareWeek(cell, hi) <= 0;
}

export function MaLieferKwRangePicker({
  value,
  disabled,
  onApply,
}: MaLieferKwRangePickerProps) {
  const initialMonth = useMemo(() => {
    if (value) return mondayOfIsoWeek(value.jahr, value.von);
    return new Date();
  }, [value]);

  const [viewMonth, setViewMonth] = useState(() =>
    startOfMonth(initialMonth.getFullYear(), initialMonth.getMonth()),
  );
  const [draftVon, setDraftVon] = useState<{ week: number; year: number } | null>(
    value ? { week: value.von, year: value.jahr } : null,
  );
  const [draftBis, setDraftBis] = useState<{ week: number; year: number } | null>(
    value ? { week: value.bis, year: value.jahr } : null,
  );

  useEffect(() => {
    if (value) {
      setDraftVon({ week: value.von, year: value.jahr });
      setDraftBis({ week: value.bis, year: value.jahr });
      setViewMonth(
        startOfMonth(
          mondayOfIsoWeek(value.jahr, value.von).getFullYear(),
          mondayOfIsoWeek(value.jahr, value.von).getMonth(),
        ),
      );
    }
  }, [value]);

  const weeks = useMemo(() => buildMonthWeeks(viewMonth), [viewMonth]);
  const monthLabel = MONTH_FORMATTER.format(viewMonth);

  const previewLabel = useMemo(() => {
    if (!draftVon) return "Wochen wählen …";
    const end = draftBis ?? draftVon;
    const lo = compareWeek(draftVon, end) <= 0 ? draftVon : end;
    const hi = compareWeek(draftVon, end) <= 0 ? end : draftVon;
    return formatLieferKw({ von: lo.week, bis: hi.week, jahr: lo.year });
  }, [draftVon, draftBis]);

  function resetDraftFromValue() {
    if (value) {
      setDraftVon({ week: value.von, year: value.jahr });
      setDraftBis({ week: value.bis, year: value.jahr });
    } else {
      setDraftVon(null);
      setDraftBis(null);
    }
  }

  function handleWeekClick(cell: WeekCell) {
    const next = { week: cell.week, year: cell.year };
    if (!draftVon || (draftVon && draftBis)) {
      setDraftVon(next);
      setDraftBis(null);
      return;
    }
    setDraftBis(next);
  }

  function handleApply(close: () => void) {
    if (!draftVon) return;
    const end = draftBis ?? draftVon;
    const lo = compareWeek(draftVon, end) <= 0 ? draftVon : end;
    const hi = compareWeek(draftVon, end) <= 0 ? end : draftVon;
    // Bereich innerhalb eines ISO-Jahres halten (Textparser erwartet ein Jahr).
    if (lo.year !== hi.year) {
      onApply({ von: lo.week, bis: lo.week, jahr: lo.year });
    } else {
      onApply({ von: lo.week, bis: hi.week, jahr: lo.year });
    }
    close();
  }

  return (
    <Popover className="relative shrink-0">
      {({ open, close }) => (
        <>
          <PopoverButton
            type="button"
            disabled={disabled}
            className={clsx(
              "inline-flex h-10 items-center justify-center rounded-md border border-separator bg-card px-2.5 text-sm text-label transition hover:bg-fill-tertiary disabled:cursor-not-allowed disabled:opacity-50",
              open && "border-accent bg-accent-soft text-accent",
            )}
            title="Kalenderwochen wählen"
            aria-label="Kalenderwochen wählen"
            onClick={() => {
              if (!open) resetDraftFromValue();
            }}
          >
            <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-5 w-5">
              <rect
                x="3"
                y="4"
                width="14"
                height="13"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M3 8h14M7 2.5v3M13 2.5v3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </PopoverButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            afterLeave={resetDraftFromValue}
          >
            <PopoverPanel className="absolute right-0 z-50 mt-2 w-[min(92vw,22rem)] rounded-ios border border-separator bg-card p-3 shadow-ios-md">
              <div className="mb-2 flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-label-secondary hover:bg-fill-tertiary hover:text-label"
                  aria-label="Vorheriger Monat"
                  onClick={() => setViewMonth((m) => addMonths(m, -1))}
                >
                  ‹
                </button>
                <div className="text-sm font-semibold capitalize text-label">
                  {monthLabel}
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-label-secondary hover:bg-fill-tertiary hover:text-label"
                  aria-label="Nächster Monat"
                  onClick={() => setViewMonth((m) => addMonths(m, 1))}
                >
                  ›
                </button>
              </div>

              <div className="mb-1 grid grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] gap-0.5 text-center text-[0.65rem] font-medium uppercase tracking-wide text-label-tertiary">
                <span>KW</span>
                {WEEKDAY_LABELS.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>

              <div className="space-y-0.5">
                {weeks.map((cell) => {
                  const inRange = isInSelectedRange(cell, draftVon, draftBis);
                  const isStart = draftVon && sameWeek(cell, draftVon);
                  const isEnd =
                    (draftBis && sameWeek(cell, draftBis)) ||
                    (draftVon && !draftBis && sameWeek(cell, draftVon));
                  const inViewMonth = cell.days.some(
                    (d) =>
                      d.getMonth() === viewMonth.getMonth() &&
                      d.getFullYear() === viewMonth.getFullYear(),
                  );
                  return (
                    <button
                      key={weekKey(cell)}
                      type="button"
                      onClick={() => handleWeekClick(cell)}
                      className={clsx(
                        "grid w-full grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] gap-0.5 rounded-md px-0.5 py-0.5 text-left transition",
                        inRange
                          ? "bg-accent-soft"
                          : "hover:bg-fill-tertiary",
                        !inViewMonth && "opacity-45",
                      )}
                    >
                      <span
                        className={clsx(
                          "flex h-7 items-center justify-center rounded text-xs font-semibold tabular-nums",
                          isStart || isEnd
                            ? "bg-accent text-white"
                            : "text-label-secondary",
                        )}
                      >
                        {cell.week}
                      </span>
                      {cell.days.map((day) => {
                        const outside =
                          day.getMonth() !== viewMonth.getMonth() ||
                          day.getFullYear() !== viewMonth.getFullYear();
                        return (
                          <span
                            key={day.toISOString()}
                            className={clsx(
                              "flex h-7 items-center justify-center rounded text-xs tabular-nums",
                              outside ? "text-label-tertiary" : "text-label",
                            )}
                          >
                            {day.getDate()}
                          </span>
                        );
                      })}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2 border-t border-separator pt-3">
                <p className="min-w-0 truncate text-xs text-label-secondary">
                  {previewLabel}
                  {!draftBis && draftVon ? (
                    <span className="text-label-tertiary"> · Ende wählen</span>
                  ) : null}
                </p>
                <button
                  type="button"
                  disabled={!draftVon}
                  className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                  onClick={() => handleApply(close)}
                >
                  Übernehmen
                </button>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
